<template>
  <div ref="root" class="center">
    <button
      type="button"
      class="bell"
      :aria-label="bellLabel"
      :aria-expanded="open"
      @click="toggle"
    >
      <font-awesome-icon :icon="['fas', 'bell']" />
      <!-- Red count: auction alerts only (a batch of watch results never
           hides an alert); a grey dot when only the watch has news -->
      <span v-if="unreadByKind.auction" class="badge">{{
        unreadByKind.auction > 9 ? "9+" : unreadByKind.auction
      }}</span>
      <span
        v-else-if="unreadByKind.monitor"
        class="badge badge--watch"
        aria-hidden="true"
      ></span>
    </button>

    <!-- Notification reçue pendant que l'app est ouverte (le téléphone ne l'affiche pas toujours) -->
    <transition name="toast">
      <button
        v-if="toast"
        type="button"
        class="toast"
        :class="{ 'toast--watch': toast.kind === 'monitor' }"
        @click="openToast"
      >
        <strong>{{ toast.title }}</strong>
        <span>{{ toast.body }}</span>
      </button>
    </transition>

    <div v-if="open" class="panel" role="dialog" aria-label="Notifications">
      <header>
        <strong>Notifications</strong>
        <span class="header-actions">
          <button
            v-if="entries.length"
            type="button"
            class="clear"
            :disabled="clearing"
            @click="clearKind"
          >
            {{ clearing ? "…" : "Vider" }}</button
          ><router-link to="/notifications" @click="open = false"
            >Réglages</router-link
          >
        </span>
      </header>
      <div class="tabs" role="tablist">
        <button
          v-for="tab in TABS"
          :key="tab.kind"
          type="button"
          role="tab"
          :aria-selected="kind === tab.kind"
          :class="['tab', `tab--${tab.kind}`, { active: kind === tab.kind }]"
          @click="showKind(tab.kind)"
        >
          {{ tab.label
          }}<b v-if="unreadByKind[tab.kind]">{{ unreadByKind[tab.kind] }}</b>
        </button>
      </div>
      <p v-if="loading && !entries.length" class="empty">Chargement…</p>
      <p v-else-if="!entries.length" class="empty">
        {{
          kind === "monitor"
            ? "Aucune notification de veille pour l’instant."
            : "Aucune alerte pour l’instant. Les alertes de fin d’enchère apparaîtront ici."
        }}
      </p>
      <ul v-else>
        <li
          v-for="entry in entries"
          :key="entry.id"
          :class="[`kind--${entry.kind}`, { unread: !entry.read_at }]"
        >
          <button type="button" @click="openEntry(entry)">
            <strong>{{ entry.title }}</strong>
            <span>{{ entry.body }}</span>
            <small>{{ relative(entry.created_at) }}</small>
          </button>
        </li>
      </ul>
      <footer>
        <router-link to="/jobs" @click="open = false"
          >Voir les échéances →</router-link
        >
      </footer>
    </div>
  </div>
</template>

<script>
import { toDate } from "../../clock";
import { openDetail, parseOpenParam } from "../../detailStack";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import config from "@/config/config.js";

const POLL_MS = 60000;
// Auction alerts first: an alert never gets lost among the watch results
const TABS = [
  { kind: "auction", label: "Enchères" },
  { kind: "monitor", label: "Veille" },
];

export default {
  name: "NotificationCenter",
  components: { FontAwesomeIcon },
  setup() {
    const router = useRouter();
    const root = ref(null);
    const open = ref(false);
    const loading = ref(false);
    const kind = ref("auction");
    const unreadByKind = ref({ auction: 0, monitor: 0 });
    const entries = ref([]);
    let timer = null;
    let request = 0;

    const bellLabel = computed(() => {
      const { auction, monitor } = unreadByKind.value;
      if (!auction && !monitor) return "Notifications";
      return [
        auction ? `${auction} alerte(s) d’enchère` : "",
        monitor ? `${monitor} notification(s) de veille` : "",
      ]
        .filter(Boolean)
        .join(", ")
        .concat(" non lue(s)");
    });

    const load = async () => {
      const current = ++request;
      loading.value = true;
      try {
        const { data } = await axios.get(`${config.apiUrl}/notifications`, {
          params: { limit: 20, kind: kind.value },
        });
        if (current !== request) return;
        unreadByKind.value = data?.unread_by_kind || {
          auction: data?.unread || 0,
          monitor: 0,
        };
        entries.value = data?.entries || [];
      } catch {
        /* silencieux : la cloche reste utilisable */
      } finally {
        if (current === request) loading.value = false;
      }
    };

    // Only the tab on screen is marked read: opening the bell for an alert
    // does not mark the watch as seen, and the other way round
    const markRead = async () => {
      const shown = kind.value;
      if (!unreadByKind.value[shown]) return;
      try {
        await axios.post(`${config.apiUrl}/notifications/read`, {
          kind: shown,
        });
        unreadByKind.value = { ...unreadByKind.value, [shown]: 0 };
      } catch {
        /* réessayé à la prochaine ouverture */
      }
    };

    // "Vider": only the tab on screen, after a confirmation
    const clearing = ref(false);
    const clearKind = async () => {
      const label = kind.value === "monitor" ? "de veille" : "d’enchère";
      if (!window.confirm(`Supprimer toutes les notifications ${label} ?`))
        return;
      clearing.value = true;
      try {
        await axios.delete(`${config.apiUrl}/notifications`, {
          params: { kind: kind.value },
        });
        entries.value = [];
        unreadByKind.value = { ...unreadByKind.value, [kind.value]: 0 };
      } catch {
        window.alert("Les notifications n’ont pas pu être supprimées.");
      } finally {
        clearing.value = false;
      }
    };

    const showKind = async (value) => {
      if (kind.value === value) return;
      kind.value = value;
      entries.value = [];
      await load();
      markRead();
    };

    const toggle = async () => {
      open.value = !open.value;
      if (open.value) {
        // Always the auctions first (the watch tab shows its own count)
        kind.value = "auction";
        entries.value = [];
        await load();
        markRead();
      }
    };

    // Lien interne (même site) : navigation dans l'app ; sinon nouvel onglet
    const openEntry = (entry) => {
      open.value = false;
      if (!entry.url) return;
      let target;
      try {
        target = new URL(entry.url, window.location.origin);
      } catch {
        return; // lien invalide
      }
      if (target.origin !== window.location.origin) {
        window.open(entry.url, "_blank", "noopener");
        return;
      }
      // Older end-of-auction alerts led to the followed listings: now to
      // Échéances, with the sheet of the offer over it
      const legacyOffer = target.searchParams.get("offer");
      if (entry.kind === "auction" && target.pathname === "/offers" && legacyOffer)
        target = new URL(
          `/jobs?open=offer:${encodeURIComponent(legacyOffer)}`,
          window.location.origin,
        );
      // Its sheet on the page already shown: opened right away (going to the
      // same address does nothing, e.g. the same notification clicked again
      // after closing its sheet)
      const sheets = parseOpenParam(target.searchParams.get("open"));
      if (sheets.length && target.pathname === router.currentRoute.value.path) {
        sheets.forEach((sheet) => openDetail(sheet.type, sheet.id));
        return;
      }
      router.push(target.pathname + target.search);
    };

    const relative = (value) => {
      const date = toDate(value);
      if (!date) return "";
      const minutes = Math.round((Date.now() - date.getTime()) / 60000);
      if (minutes < 1) return "à l’instant";
      if (minutes < 60) return `il y a ${minutes} min`;
      if (minutes < 1440) return `il y a ${Math.round(minutes / 60)} h`;
      return new Intl.DateTimeFormat("fr-FR", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
    };

    const onClickOutside = (event) => {
      if (open.value && root.value && !root.value.contains(event.target))
        open.value = false;
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };

    // Message du service worker : une notification push vient d'arriver
    const toast = ref(null);
    let toastTimer = null;
    const onPushMessage = (event) => {
      if (event.data?.type !== "push") return;
      toast.value = event.data.payload || {};
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => (toast.value = null), 10000);
      // Only an auction alert vibrates (the watch stays quiet)
      if (navigator.vibrate && toast.value.kind !== "monitor")
        navigator.vibrate([200, 100, 200]);
      load();
    };
    const openToast = () => {
      const entry = { url: toast.value?.url, kind: toast.value?.kind };
      toast.value = null;
      openEntry(entry);
    };

    onMounted(() => {
      load();
      timer = setInterval(load, POLL_MS);
      navigator.serviceWorker?.addEventListener("message", onPushMessage);
      document.addEventListener("click", onClickOutside);
      document.addEventListener("visibilitychange", onVisible);
    });
    onBeforeUnmount(() => {
      clearInterval(timer);
      clearTimeout(toastTimer);
      navigator.serviceWorker?.removeEventListener("message", onPushMessage);
      document.removeEventListener("click", onClickOutside);
      document.removeEventListener("visibilitychange", onVisible);
    });

    return {
      TABS,
      bellLabel,
      clearKind,
      clearing,
      entries,
      kind,
      loading,
      open,
      openEntry,
      openToast,
      toast,
      relative,
      root,
      showKind,
      toggle,
      unreadByKind,
    };
  },
};
</script>

<style scoped>
.toast {
  position: fixed;
  top: calc(12px + var(--safe-top));
  left: 50%;
  z-index: 200;
  display: flex;
  width: min(420px, calc(100vw - 24px));
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border: 1px solid #b9d4ee;
  border-left: 4px solid #c84642;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 18px 45px rgba(15, 23, 36, 0.25);
  text-align: left;
  transform: translateX(-50%);
  cursor: pointer;
}
.toast--watch {
  border-left-color: #2e6098;
}
.toast strong {
  color: #1d2939;
  font-size: 14px;
}
.toast span {
  color: #536073;
  font-size: 13px;
  white-space: pre-line;
}
.toast-enter-active,
.toast-leave-active {
  transition:
    opacity 0.2s,
    transform 0.2s;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -10px);
}
.center {
  position: relative;
}
.bell {
  position: relative;
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border: 1px solid #dde3e9;
  border-radius: 8px;
  background: #fff;
  color: #596577;
  cursor: pointer;
}
.badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background: #c84642;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  line-height: 18px;
  text-align: center;
}
.header-actions {
  display: inline-flex;
  align-items: center;
  gap: 14px;
}
.panel .clear {
  padding: 0;
  border: 0;
  background: none;
  color: #b42318;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.badge--watch {
  top: -3px;
  right: -3px;
  min-width: 10px;
  width: 10px;
  height: 10px;
  padding: 0;
  background: #8994a3;
}
.tabs {
  display: flex;
  gap: 4px;
  padding: 8px 10px 0;
  border-bottom: 1px solid #edf0f4;
}
.tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: none;
  color: #596577;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.tab.active {
  border-bottom-color: currentColor;
}
.tab--auction.active {
  color: #b45309;
}
.tab--monitor.active {
  color: #2e6098;
}
.tab b {
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #c84642;
  color: #fff;
  font-size: 10px;
  line-height: 18px;
  text-align: center;
}
.tab--monitor b {
  background: #8994a3;
}
.panel {
  position: absolute;
  top: 46px;
  right: 0;
  z-index: 60;
  width: min(360px, calc(100vw - 24px));
  max-height: 70vh;
  overflow: auto;
  border: 1px solid #dfe5ec;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 18px 45px rgba(15, 23, 36, 0.18);
}
.panel header,
.panel footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
}
.panel header {
  border-bottom: 1px solid #edf0f4;
}
.panel footer {
  border-top: 1px solid #edf0f4;
}
.panel a {
  color: #2e6098;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}
.empty {
  margin: 0;
  padding: 18px 14px;
  color: #7a8697;
  font-size: 13px;
}
.panel ul {
  margin: 0;
  padding: 0;
  list-style: none;
}
.panel li {
  display: block;
  margin: 0;
}
.panel li + li {
  border-top: 1px solid #f0f2f5;
}
.panel li button {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 3px;
  padding: 10px 14px;
  border: 0;
  background: none;
  text-align: left;
  cursor: pointer;
}
.panel li button:hover {
  background: #f7fafc;
}
.panel li.unread button {
  background: #f3f9f6;
}
/* End-of-auction alerts stand out in orange */
.panel li.kind--auction button {
  border-left: 3px solid #e08a1e;
}
.panel li.kind--auction.unread button {
  background: #fff6ea;
}
.panel li strong {
  color: #1d2939;
  font-size: 13px;
}
.panel li span {
  color: #536073;
  font-size: 12px;
  white-space: pre-line;
}
.panel li small {
  color: #8994a3;
  font-size: 11px;
}
/* Small screens: the bell is not at the edge of the screen (the "+" button
   is on its right), so the panel is laid on the screen, not on the bell */
@media (max-width: 560px) {
  .panel {
    position: fixed;
    top: calc(64px + var(--safe-top));
    right: 12px;
    left: 12px;
    width: auto;
    max-height: calc(100vh - 88px - var(--safe-top) - var(--safe-bottom));
  }
}
</style>
